import config from "../config";
import * as _ from 'lodash';
import {buildUrlFromConfig, getData, getProperty} from './http-request-utils';
import {httpDelete, httpGet, httpPatch, httpPost} from "./http-functions";
import {OrganizationUnit, ShipTo} from "../models/organization";
import {DisplayedSubscriptionCount, SubscriptionCount} from "../models/subscription-model";
import {MemberToAdd} from "../models/membership.models";
import {convertDateStringToLocaleJsonDate} from "../utils/function-util";

interface FilterableCollectionArgs {
    size?: number
    page?: number
    filter?: string
}

function buildMembershipUser(result: any) {
    return ({
        id: result.userId,
        firstName: result.userFirstName,
        lastName: result.userLastName,
        email: result.email,
        isActive: result.isActive
    });
}

export const getTopOrganizationUnits = async (organizationId: number, permission: any = null): Promise<OrganizationUnit[]> => {
    const resp = await httpGet(
        `${config.api}/organizations/${organizationId}/organization-units/tops?permission=${permission}`);

    return resp.data;
};

export const getOrganizationUnit = async (organizationId: number, organizationUnitId: number): Promise<OrganizationUnit> => {
    const resp = await httpGet(
        `${config.api}/organizations/${organizationId}/organization-units/${organizationUnitId}`);

    return resp.data;
};

export const renameOrganizationUnit = async (unit: OrganizationUnit, name: string): Promise<OrganizationUnit> => {
    const resp = await httpPatch(
        `${config.api}/organizations/${unit.organizationId}/organization-units/${unit.id}`,
        {name}
    );
    return resp.data;
};

export const getOrganizationUnitChildren = async (organizationId: number, organizationUnitId: number): Promise<OrganizationUnit[]> => {
    const resp = await httpGet(
        `${config.api}/organizations/${organizationId}/organization-units/${organizationUnitId}/children`);

    return resp.data;
};

export const searchOrganizationUnitChildren = async (organizationId: number, organizationUnitId: number, from: number, size: number, filter: string): Promise<OrganizationUnit[]> => {
    const url = `${config.api}/organizations/${organizationId}/organization-units/${organizationUnitId}/children/search`;
    const resp = await httpPost(url, {filter, from, size});

    return resp.data;
};

export const getOrganizationUnitCount = async (organizationId: number, organizationUnitId: number, filter: string = ''): Promise<number> => {
    const url = `${config.api}/organizations/${organizationId}/organization-units/${organizationUnitId}/children/count`;
    const resp = await httpGet(url, {params: {filter}});

    return getProperty('total')(resp) || 0;
};

export const getSubscriptionsCount = async (organizationId: number, organizationUnitId: number, recursive = false): Promise<SubscriptionCount[]> => {
    const resp = await httpGet(
        `${config.api}/organizations/${organizationId}/organization-units/${organizationUnitId}/subscriptions-count`,
        {
            params: {
                recursive
            }
        }
    );

    return resp.data;
};

export const getDisplayedSubscriptionsCount = async (organizationId: number, organizationUnitId: number, recursive: boolean = false): Promise<DisplayedSubscriptionCount[]> => {
    const availabilityCounts = await getSubscriptionsCount(organizationId, organizationUnitId, recursive);

    const allSubscriptions = {
        type: 'All',
        available: availabilityCounts.reduce((acc: number, curr: SubscriptionCount) => acc + curr.availableSubscriptions, 0),
        used: availabilityCounts.reduce((acc: number, curr: SubscriptionCount) => acc + curr.usedSubscriptions, 0),
    };

    const displayedSubscriptionsCounts = [
        {...allSubscriptions, total: allSubscriptions.available + allSubscriptions.used},
        ...availabilityCounts
            .map((ac: any) => ({
                type: ac.subscriptionType,
                available: ac.availableSubscriptions,
                used: ac.usedSubscriptions,
                total: ac.availableSubscriptions + ac.usedSubscriptions
            }))
    ];

    return displayedSubscriptionsCounts;
};

export const addOrganizationUnit = async (organizationId: number, organizationUnitId: number, name: string): Promise<{ items: OrganizationUnit[] }> => {
    const resp = await httpPost(
        `${config.api}/organizations/${organizationId}/organization-units/${organizationUnitId}`, {items: [{name}]});
    return resp.data;
};

export const deleteOrganizationUnit = async (organizationId: number, organizationUnitId: number): Promise<string[]> => {
    const resp = await httpDelete(
        `${config.api}/organizations/${organizationId}/organization-units/${organizationUnitId}`)
    return getData(resp);
};

export const getMemberships = async (organizationId: number, organizationUnitId: number, opt: FilterableCollectionArgs) => {
    const url = buildUrlFromConfig({organizationId, organizationUnitId, path: 'memberships/search'});
    const defaults = {filter: '', page: 0, size: 100};
    const result = await httpPost(url, {
        ...defaults,
        ...opt
    });

    return ({
        ...opt,
        total: getProperty('total')(result),
        items: getProperty('items')(result)
    });
}
export const getAllMemberships = async (organizationId: number, organizationUnitId: number, batchSize: number = 100) => {
    let allItems: any[] = [];
    let total = 0;
    let page = 0;
    let lastBatchHasContent: boolean;

    do {
        const result = await getMemberships(organizationId, organizationUnitId, {
            page: page++,
            size: batchSize
        });
        lastBatchHasContent = Array.isArray(result.items) && result.items.length > 0;
        allItems = allItems.concat(result.items.map(buildMembershipUser));
        total = result.total;
    } while((page * batchSize < total) && lastBatchHasContent)

    return {
        items: allItems,
        total: allItems.length
    }
};

export const searchMembership = async (organizationId: number, organizationUnitId: number, opt: FilterableCollectionArgs): Promise<any> => {
    function getSyntheticSubscriptionType(subscriptionType: any) {
        if (!subscriptionType) return 'no-subscription';
        switch (subscriptionType) {
            case 'standard':
                return 'Standard';
            case 'provider-15':
            case 'provider-50':
            case 'provider-100':
                return 'Pro';
            case 'embedded':
                return 'Machine';
            default:
                return 'NoSubscription';
        }
    }

    function getOwnershipStatus(ownershipStatus: any) {
        switch (ownershipStatus) {
            case 'pending':
                return _.capitalize(ownershipStatus);
            case 'accepted':
                return 'Active';
            default:
                return null;
        }
    }

    function buildMembershipSubscription(memberShipResult: any) {
        return memberShipResult.subscriptions.map((subscription: any) => ({
            id: subscription.id,
            userId: subscription.userId,
            type: getSyntheticSubscriptionType(subscription.type),
            source: subscription.source,
            expirationDate: convertDateStringToLocaleJsonDate(subscription.expirationDate),
            isExpired: subscription.isExpired
        }));
    }

    function buildSubscriptionOwnership(memberShipResult: any) {
        return ({
            status: getOwnershipStatus(memberShipResult.accountOwnershipStatus)
        });
    }

    function buildSearchMembershipResult(memberShipResult: any) {
        return ({
            id: memberShipResult.userId,
            user: buildMembershipUser(memberShipResult),
            subscriptions: buildMembershipSubscription(memberShipResult),
            ownership: buildSubscriptionOwnership(memberShipResult)
        });
    }

    const processItems = _.flow(
        _.partialRight(_.reduce,
            (acc: any, cur: any) => {
                acc[cur.userId] = buildSearchMembershipResult(cur);
                return acc;
            }, {}),
        _.values);

        const result = await getMemberships(organizationId, organizationUnitId, opt);

        return {
            ...result,
            items: processItems(result.items)
        }
};

export const getAncestors = (httpFn: any) => async ({
                                                        organizationId,
                                                        organizationUnitId,
                                                        recursive = false
                                                    }: { organizationId: number, organizationUnitId: number, recursive: boolean }): Promise<OrganizationUnit[]> => {
    const url = buildUrlFromConfig({organizationId, organizationUnitId, path: 'ancestors'});
    const result = await httpFn(url, {params: {recursive}});
    return getData(result);
};

export const assignSubscription = async (organizationId: number, organizationUnitId: number, subscription: MemberToAdd & { forceAssign: boolean }) => {
    const resp = await httpPost(
        `${config.api}/Organizations/${organizationId}/organization-units/${organizationUnitId}/subscriptions`, subscription);

    return resp.data;
};

export const assignAccountAndSubscription = async (organizationId: number, organizationUnitId: number, accountAndSubscription: MemberToAdd) => {
    const resp = await httpPost(
        `${config.api}/Organizations/${organizationId}/organization-units/${organizationUnitId}/accounts`, accountAndSubscription);
    return getData(resp);
};

export const addMember = async (organizationId: number, organizationUnitId: number, membership: MemberToAdd) => {
    const resp = await httpPost(
        `${config.api}/Organizations/${organizationId}/organization-units/${organizationUnitId}/memberships`, membership);

    return getData(resp);
};

export const removeMember = async (organizationId: number, organizationUnitId: number, userId: string): Promise<{ success: boolean }> => {
    const resp = await httpDelete(
        `${config.api}/Organizations/${organizationId}/organization-units/${organizationUnitId}/memberships/${userId}`);
    return getData(resp);
};

export const unassignSubscription = async (organizationId: number, organizationUnitId: number, subscriptionId: number): Promise<void> => {
    const resp = await httpPost(
        `${config.api}/Organizations/${organizationId}/organization-units/${organizationUnitId}/subscriptions/${subscriptionId}/unassign`);
    return getData(resp);
};

export const moveMember = async (organizationId: number, sourceOrganizationUnitId: number, targetOrganizationUnitId: number, userId: string) =>{
    const resp = await httpPost(
        `${config.api}/Organizations/${organizationId}/organization-units/${sourceOrganizationUnitId}/transfer-membership/${targetOrganizationUnitId}`,
        {userId}
    );
    return getData(resp);
};

export const getShipTos = async (organizationId: number, orgUnitId: number): Promise<ShipTo[]> => {
    const resp = await httpGet(`${config.api}/Organizations/${organizationId}/organization-units/${orgUnitId}/shiptos`);
    return resp.data;
}

export const addShipTo = async (organizationId: number, orgUnitId: number, shipToNumber?: number): Promise<ShipTo> => {
    const resp = await httpPost(`${config.api}/Organizations/${organizationId}/organization-units/${orgUnitId}/shiptos`, {organizationId, orgUnitId, shipToNumber});
    return resp.data;
}

export const deleteShipTo = async (organizationId: number, orgUnitId: number, id: number): Promise<boolean> => {
    const resp = await httpDelete(`${config.api}/Organizations/${organizationId}/organization-units/${orgUnitId}/shiptos/${id}`);
    return resp.data;
}
